import React, { useState } from 'react';
import Box from '@mui/material/Box';
import {
  CategoryColumnDefinition,
  SubcategoryColumnDefinition,
  BrandColumnDefinition
} from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import ProductGrid from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import { useAppSelector } from 'src/utils/Hooks';
import { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import { BEACON_KEY_METRIC_CARD_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import AtlasV2MetricListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2MetricListContainer';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';

enum DropdownOption {
  Product = 'stacklineSku',
  Category = 'categoryId',
  Subcategory = 'subCategoryId',
  Brand = 'brandId'
}

/**
 * Table definition by the selected dropdown
 */
const dropdownOptionToColumnDefinition = {
  [DropdownOption.Product]: {
    headerName: 'Products',
    valueFormatter: (_, row: { product: any }) => (
      <div style={{ width: '547px' }}>
        <ProductCell key={row.product.stacklineSku} stacklineSku={row.product.stacklineSku} title={row.product.title} />
      </div>
    )
  },
  [DropdownOption.Category]: CategoryColumnDefinition,
  [DropdownOption.Subcategory]: SubcategoryColumnDefinition,
  [DropdownOption.Brand]: BrandColumnDefinition
};

/**
 * Dropdown option names
 */
const dropdownOptionToDisplayName = {
  [DropdownOption.Product]: 'Product',
  [DropdownOption.Category]: 'Category',
  [DropdownOption.Subcategory]: 'Subcategory',
  [DropdownOption.Brand]: 'Brand'
};

/**
 * Shows the dropdown grid on the summary page
 */
const AtlasV2KeyMetricsGrid = () => {
  const [dropdownOption, setDropdownOption] = useState<DropdownOption>(DropdownOption.Product);
  const entityType = useAppSelector((state) => state.entityService.mainEntity.type);

  const pageSizeByDropdownOption = {
    [DropdownOption.Subcategory]: 100,
    [DropdownOption.Category]: 100,
    [DropdownOption.Brand]: 100
  };

  /**
   * Dropdown options by the main entity type
   */
  const dropdownOptionsByEntityType = {
    category: [DropdownOption.Product, DropdownOption.Subcategory, DropdownOption.Brand],
    subcategory: [DropdownOption.Product, DropdownOption.Brand]
  };

  return (
    <Box sx={{ marginTop: `${BEACON_KEY_METRIC_CARD_SPACING}px` }}>
      <AtlasV2MetricListContainer
        entityTableHeaderProps={{
          prefixTitle: 'Retail Sales by',
          title: '',
          groupByFields: (
            dropdownOptionsByEntityType[entityType] || [
              DropdownOption.Product,
              DropdownOption.Category,
              DropdownOption.Subcategory,
              DropdownOption.Brand
            ]
          ).map((option) => ({
            name: option,
            displayName: dropdownOptionToDisplayName[option]
          })),
          handleGroupByChange: (event) => setDropdownOption(event.target.value as DropdownOption)
        }}
        fields={[
          {
            name: 'retailSales'
          },
          {
            name: 'retailPrice'
          },
          {
            name: 'unitsSold'
          }
        ]}
        indexName="sales"
        sortFieldName="retailSales"
        showGrid={dropdownOption === DropdownOption.Product}
        useTableDataProps={{
          groupByFieldName: dropdownOption,
          filterResponse: () => true,
          itemsPerPage: pageSizeByDropdownOption[dropdownOption] || 100
        }}
        headColumnDefinition={dropdownOptionToColumnDefinition[dropdownOption]}
        renderGrid={dropdownOption === DropdownOption.Product ? (args) => <ProductGrid {...args} /> : undefined}
        showSortOptions
        iconList={[IconsList.export, IconsList.tile, IconsList.table]}
        appTableProps={{
          sx: {
            'tr td:first-child': {
              ...(dropdownOption === DropdownOption.Product
                ? {
                    width: '612px',
                    paddingRight: '40px'
                  }
                : {})
            }
          }
        }}
      />
    </Box>
  );
};

export default AtlasV2KeyMetricsGrid;
